import Axios from "axios";
import openDrawer from "./openDrawer";
import useAuthStore from "../../stores/useAuthStore";

const DayEndReportPrint = async (printReciptData) => {
  const tillData = useAuthStore.getState().tillData;
  const printerURL = tillData?.tillAccess?.cwrTill?.hardwareController?.imageUrl;
  const pricePrecision = tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision ?? 2;

  // Embedded dayEndTemplate as a string with each <text> tag on a separate line
  let printData = `
<output>
<ticket>
<line></line>
<line>
  <text align="center" length="42" bold="true">DAY END CLOSING REPORT</text>
</line>
<line></line>
<line>
  <text align="center" length="42">Branch: @@branch@@</text>
</line>
<line>
  <text align="center" length="42">Opened At: @@openingTime@@</text>
</line>
<line>
  <text align="center" length="42">Closed At: @@closingTime@@</text>
</line>
<line>
  <text align="center" length="42">User: @@userName@@</text>
</line>
<line></line>
<line>
  <text align="left" length="42" bold="true">Tax Details</text>
</line>
<line>
  <text align="left" length="20">Tax Type</text>
  <text align="left" length="11">NET</text>
  <text align="left" length="11">Tax</text>
</line>
@@taxDetails@@
<line>
  <text align="left" length="42">Gross Total: @@grossTotal@@</text>
</line>
<line></line>
<line>
  <text align="left" length="42" bold="true">Details of Payment</text>
</line>
<line>
  <text align="left" length="20" bold="true">Mode of Payment</text>
  <text align="center" length="10" bold="true">Inv Count</text>
  <text align="left" length="12" bold="true">Gross Amt</text>
</line>
@@paymentDetails@@
<line></line>
<line>
  <text align="left" length="42" bold="true">Cash Account</text>
</line>
<line>
  <text align="left" length="15" bold="true">Mode</text>
  <text align="left" length="9" bold="true">Actual</text>
  <text align="left" length="9" bold="true">System</text>
  <text align="left" length="9" bold="true">Diff</text>
</line>
@@cashAccount@@
<line></line>
<line>
  <text align="left" length="42" bold="true">Note Counting</text>
</line>
<line>
  <text align="left" length="15" bold="true">Denomination</text>
  <text align="center" length="10" bold="true">Quantity</text>
  <text align="left" length="17" bold="true">Total</text>
</line>
@@noteCounting@@
<line></line>
<line>
  <text align="left" length="25" bold="true">Total Amount</text>
  <text align="right" length="14" bold="true">@@totalAmount@@</text>
</line>
<line></line>
<line>
  <text align="left" length="42" bold="true">Cash Summary</text>
</line>
<line>
  <text align="left" length="25" bold="true">Opening Balance</text>
  <text align="right" length="14" bold="true">@@openingBalance@@ AED</text>
</line>
<line>
  <text align="left" length="25" bold="true">Cash Sales</text>
  <text align="right" length="14" bold="true">@@cashSales@@</text>
</line>
<line>
  <text align="left" length="25" bold="true">Cash In</text>
  <text align="right" length="14" bold="true">@@cashIn@@</text>
</line>
<line>
  <text align="left" length="25" bold="true">Cash Out</text>
  <text align="right" length="14" bold="true">@@cashOut@@</text>
</line>
<line>
  <text align="left" length="25" bold="true">Closing Balance</text>
  <text align="right" length="14" bold="true">@@closingBalance@@</text>
</line>
<line>
  <text align="left" length="25" bold="true">Diff</text>
  <text align="right" length="14" bold="true">@@diff@@</text>
</line>
<line></line>
<line>
  <text align="left" length="25" bold="true">Cash To Keep</text>
  <text align="right" length="14" bold="true">@@carryForward@@</text>
</line>
<line></line>
<line />
<line />
<line />
</ticket>
</output>
`;

  if (!printData) {
    return printData;
  }

  // Build taxDetails dynamically
  let taxDetails = "";
  printReciptData.taxDetailsSummary.forEach((tax) => {
    taxDetails += `<line>
    <text align="left" length="20">${tax.taxType}</text>
    <text align="left" length="11">${parseFloat(tax.net).toFixed(pricePrecision)}</text>
    <text align="left" length="11">${parseFloat(tax.tax).toFixed(pricePrecision)}</text>
  </line>`;
  });

  // Build paymentDetails dynamically
  let paymentDetails = "";
  printReciptData.paymentsSummary.forEach((data) => {
    paymentDetails += `<line>
    <text align="left" length="20">${data.paymentName}</text>
    <text align="center" length="10">${data.orderCount}</text>
    <text align="left" length="12">${parseFloat(data.totalAmount).toFixed(pricePrecision)}</text>
  </line>`;
  });

  // Build cashAccount dynamically (assuming non-cash have actual == system, diff == 0; cash uses specific fields)
  let cashAccount = "";
  printReciptData.paymentsSummary.forEach((data) => {
    let actual = parseFloat(data.totalAmount).toFixed(pricePrecision);
    let systemVal = parseFloat(data.totalAmount).toFixed(pricePrecision);
    let diff = (0).toFixed(pricePrecision);
    if (data.paymentName === "Cash") {
      actual = parseFloat(printReciptData.closingBalance).toFixed(pricePrecision);
      systemVal = parseFloat(printReciptData.systemClosingbalance).toFixed(pricePrecision);
      diff = parseFloat(printReciptData.closingBalanceDiff).toFixed(pricePrecision);
    }
    cashAccount += `<line>
    <text align="left" length="15">${data.paymentName}</text>
    <text align="left" length="9">${actual}</text>
    <text align="left" length="9">${systemVal}</text>
    <text align="left" length="9">${diff}</text>
  </line>`;
  });

  // Build noteCounting dynamically (sorted descending by value)
  let noteCounting = "";
  printReciptData.denominationsSummary
    .sort((a, b) => b.value - a.value)
    .forEach((data) => {
      let label = data.value.toFixed(2);
      noteCounting += `<line>
    <text align="left" length="15">${label}</text>
    <text align="center" length="10">${data.count}</text>
    <text align="left" length="17">${parseFloat(data.amount).toFixed(pricePrecision)}</text>
  </line>`;
    });

  // Calculate cashOut
  const cashOut = (parseFloat(printReciptData.cashInOutSummary.cashOut) + parseFloat(printReciptData.cashInOutSummary.pettCashOut)).toFixed(pricePrecision);

  // Calculate cashIn
  const cashIn = (parseFloat(printReciptData.cashInOutSummary.cashIn) + parseFloat(printReciptData.cashInOutSummary.pettCashIn)).toFixed(pricePrecision);

  // Replace placeholders in the template
  printData = printData.replaceAll("@@branch@@", `${printReciptData.branch}`);
  printData = printData.replaceAll("@@openingTime@@", `${printReciptData.openedAt}`);
  printData = printData.replaceAll("@@closingTime@@", `${printReciptData.closedAt}`);
  printData = printData.replaceAll("@@userName@@", `${printReciptData.userName}`);
  printData = printData.replaceAll("@@grossTotal@@", parseFloat(printReciptData.taxGrossTotal).toFixed(pricePrecision));
  printData = printData.replaceAll("@@paymentDetails@@", paymentDetails);
  printData = printData.replaceAll("@@taxDetails@@", taxDetails);
  printData = printData.replaceAll("@@cashAccount@@", cashAccount);
  printData = printData.replaceAll("@@noteCounting@@", noteCounting);
  printData = printData.replaceAll("@@totalAmount@@", parseFloat(printReciptData.denominationsTotal).toFixed(pricePrecision));
  printData = printData.replaceAll("@@openingBalance@@", parseFloat(printReciptData.openingBalance).toFixed(pricePrecision));
  printData = printData.replaceAll("@@cashSales@@", parseFloat(printReciptData.cashSales).toFixed(pricePrecision));
  printData = printData.replaceAll("@@cashOut@@", cashOut);
  printData = printData.replaceAll("@@cashIn@@", cashIn);
  printData = printData.replaceAll("@@closingBalance@@", parseFloat(printReciptData.systemClosingbalance).toFixed(pricePrecision));
  printData = printData.replaceAll("@@diff@@", parseFloat(printReciptData.closingBalanceDiff).toFixed(pricePrecision));
  printData = printData.replaceAll("@@carryForward@@", parseFloat(printReciptData.closingBalanceCarryForward).toFixed(pricePrecision));

  Axios.post(`${printerURL}printer`, printData, {
    headers: {
      "Content-Type": "application/xml; charset=UTF-8",
      Accept: "application/xml",
    },
  })
    .then(() => {
      console.debug("XML print success !");
      openDrawer();
    })
    .catch((response) => {
      console.debug("XML print failed !", response);
    });
};

export default DayEndReportPrint;
